import Commons from '../shared/libraries/commons';
import { BaseSerializer } from '/src/app/core/serializers/baseSerializer';
import constants from '@core/utils/constants';

class TransactionSerializer extends BaseSerializer {
  deserialize(raw, isLatestTransations) {
    const serverRes = raw.data || raw.error;
    const success = serverRes.statusCode == 200 || serverRes.statusCode == 404;
    if (success) {
      return {
        statusCode: 200,
        success: true,
        message: 'List got',
        data: {
          list: (serverRes?.data || []).map(formatTrx),
          total: serverRes?.total || 0,
          limit: serverRes?.limit || 100,
          offset: serverRes?.offset || 0
        }
      };
    }
    return {
      statusCode: serverRes.statusCode || raw.status || 500,
      success: false,
      message:
        (serverRes.message || 'Erreur lors de la récupération') +
        ` Code d'erreur: TRX_ERR_${
          serverRes.statusCode || raw.status || '1000'
        }`,
      data: null
    };
  }

  serialize() {}

  deserializeTransactoinDetail(raw) {
    const serverRes = raw.data || raw.error;
    const success = serverRes.statusCode == 200 || serverRes.statusCode == 404;
    if (!success) {
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message:
          (serverRes.message || 'Erreur lors de la récupération') +
          ` Code d'erreur: TRX_ERR_${
            serverRes.statusCode || raw.status || '1000'
          }`,
        data: null
      };
    }

    return {
      statuCode: 200,
      success: true,
      message: 'Found',
      data: formatTrx(serverRes.data)
    };
  }
}

function formatTrx(trx) {
  if (!trx) return null;

  const serviceId = trx.metadata?.trxService;
  const serviceInfo = constants.TRANSACTIONS.SERVICES_LIST.find(
    (s) => s.id === serviceId
  );
  const serviceName = serviceInfo?.name || serviceId || 'Service';
  const serviceLogo = serviceInfo?.logo || '';

  const operation = trx.metadata?.trxServiceOperation;
  const operationData = constants.TRANSACTIONS.SERVICES_OPTIONS[
    serviceId
  ]?.find((o) => o.id === operation);

  const operationName = operationData?.title || '-';

  // Helper to find data in either src or dst
  const getFromTrx = (field) => trx.src?.[field] || trx.dst?.[field];

  const bookmakerId = getFromTrx('playerId');
  const phoneNumber = getFromTrx('phoneNumber');
  const withdrawalCode = getFromTrx('withdrawalCode');

  return {
    /** Identité */
    id: trx.id,
    bookmakerId: bookmakerId || trx.bookmakerId,
    service: serviceId,
    bookmaker: trx.src?.bookmaker || trx.dst?.bookmaker || trx.bookmaker,
    operation,
    withdrawalCode,

    /** Affichage */
    logo: serviceLogo,
    operationNameDisplay: operationName,

    /** Dates */
    startDate: Commons.time(trx.createdAt, 'DD MMM YYYY - HH:mm'),
    createdAt: trx.createdAt,

    /** Montants */
    amountToSend: trx.src?.amount,
    currencyToSend: trx.src?.currency?.toUpperCase() || 'XOF',

    amountToReceive: trx.dst?.amount,
    currencyToReceive: trx.dst?.currency?.toUpperCase() || 'XOF',

    fee: trx.metadata?.amounts?.fee || 0,
    feeCurrency: 'XOF',

    earning: trx.metadata?.amounts?.earning || 0,
    bonus: trx.metadata?.amounts?.bonus || 0,

    /** Détail des frais */
    feeBreakdown: {
      earning: trx.metadata?.amounts?.earning || 0,
      bonus: trx.metadata?.amounts?.bonus || 0,
      fee: trx.metadata?.amounts?.fee || 0
    },

    status: trx.status || 'unknown',
    statusDisplay: statusDisplay?.[trx.status] || 'Inconnu',
    statusDetailed: statusDetailed?.[trx.status] || null,

    collectStatus: trx.srcStatus || 'unknown',
    collectStatusDisplay: statusDisplay?.[trx.srcStatus] || 'Inconnu',
    collectStatusDetailed: statusDetailed?.[trx.srcStatus] || null,

    depositStatus: trx.dstStatus || 'unknown',
    depositStatusDisplay: statusDisplay?.[trx.dstStatus] || 'Inconnu',
    depositStatusDetailed: statusDetailed?.[trx.dstStatus] || null,

    /** Utilisateur */
    senderFirstName: trx.firstName,
    senderLastName: trx.lastName,
    senderEmail: trx.email||trx.metadata?.email,
    senderPhoneNumber: phoneNumber || trx.metadata?.phoneNumber,
    senderCountryCode: trx.country,

    /** Paiement */
    paymentMethod: trx.metadata?.processor || 'automatic',
    senderPhoneNumberUsed: trx.src?.phoneNumber || trx.metadata?.senderPhoneNumber,
    recipientPhoneNumber: trx.dst?.phoneNumber,

    /** Métadonnées utiles */
    operationDetails: getOperationDetail({ ...trx, operation, serviceName, serviceLogo }),

    /** Brut pour vue détail */
    rawData: trx
  };
}

function getOperationDetail(trx) {
  const serviceName = trx.serviceName || 'Service';
  const serviceLogo = trx.serviceLogo || '';
  const serviceId = trx.metadata?.trxService || trx.service;

  if (trx.operation === 'fund') {
    return {
      collectName: 'Paiement',
      depositName: `Recharge ${serviceName}`,
      paymentMethod: trx.metadata?.processor || 'automatic',
      service: serviceId,
      serviceName,
      serviceLogo,
      bookmaker: trx.dst?.bookmaker || trx.src?.bookmaker || trx.bookmaker
    };
  }
  if (trx.operation === 'withdraw') {
    return {
      collectName: `Retrait ${serviceName}`,
      depositName: `Dépôt sur ${serviceName}`,
      paymentMethod: trx.metadata?.processor || 'automatic',
      service: serviceId,
      serviceName,
      serviceLogo,
      bookmaker: trx.src?.bookmaker || trx.dst?.bookmaker || trx.bookmaker
    };
  }
  return {};
}

const statusGroup = {
  error: 'error',
  failed: 'error',
  created: 'pending',
  pending: 'pending',
  lockedInPending: 'pending',
  success: 'success',
  paid: 'success',
  refunded: 'error',
  unpaid: 'pending',
  collected: 'pending',
  paidFailed: 'pending',
  initiated: 'pending',
  unstarted: 'pending'
};

const statusDisplay = {
  success: 'Réussi',
  paid: 'Réussi',
  validated: 'Réussi',
  error: 'Échouée',
  failed: 'Échouée',
  created: 'Créé',
  pending: 'En cours',
  unpaid: 'Recharge en cours',
  refunded: 'Remboursé',
  collected: 'En cours',
  paidFailed: 'En cours',
  initiated: 'Initié',
  unstarted: 'En attente',
  funding_failed: 'Echec de dépot',
  not_subscribed: 'Abonnement échoué',
  transaction_timeout: 'Transaction expirée sans confirmation du paiement',
  transaction_failed: "Le paiement n'a pas été confirmé",
  failed_amount_missmatch:
    "Le montant entre votre transaction et l'opérateur sont différent",
  merchant_balance_is_low: 'Erreur',
  subscriber_balance_is_low: 'Le solde du compte est insuffisant',
  not_enough_money: 'Pas assez de fonds sur le wallet Kingrecharges',
  min_amount_error: 'Le montant entré est inférieur au minimum autorisé',
  failed_to_launch_collect: "Echec d'initiation de la récupération de fond",
  failed_to_launch_deposit: "Echec d'initiation du recharge",
  bad_transaction_data: 'Les données de la transaction ne sont pas correcte',
  bad_otp_code: "Le code otp entré n'est pas correct",
  same_operation_in_short_period:
    'Une transaction avec les même données a été lancée récement. Veuillez patienter puis relancer dans quelqeus minutes'
};

const statusDetailed = {
  success: 'Transaction effectué avec succès',
  paid: 'Transaction effectué avec succès',
  validated: 'Transaction effectué avec succès',

  error: 'L’opération a échoué',
  failed: 'L’opération a échoué',

  created: 'Transaction initiée et en attente de traitement',
  pending: 'Transaction en cours de traitement',

  unpaid: 'Recharge initié, en attente de confirmation',
  refunded: 'Montant remboursé au client',

  collected: 'Paiement réussi, confirmation en attente',
  paidFailed: 'Paiement réussi, confirmation en attente',
  initiated: 'Transaction initiée',
  unstarted: 'En attente de démarrage',
  funding_failed: 'Échec lors de la tentative de dépôt',
  not_subscribed: 'Échec : l’abonnement n’a pas été activé',

  transaction_timeout:
    'La transaction a expiré faute de confirmation du paiement',
  transaction_failed: 'Le paiement n’a pas pu être confirmé',

  failed_amount_missmatch:
    'Incohérence détectée : le montant envoyé ne correspond pas à celui de l’opérateur',

  merchant_balance_is_low: 'Solde insuffisant sur le compte marchand',
  subscriber_balance_is_low: 'Solde insuffisant sur le compte utilisateur',

  not_enough_money: 'Fonds insuffisants sur le wallet',

  min_amount_error: 'Le montant saisi est inférieur au minimum autorisé',

  failed_to_launch_collect:
    'Impossible d’initier la demande de collecte de fonds',
  failed_to_launch_deposit: 'Impossible d’initier la demande de dépôt',

  bad_transaction_data:
    'Les informations fournies pour la transaction sont incorrectes ou incomplètes',

  bad_otp_code: 'Le code OTP saisi est invalide. Veuillez réessayer.',

  same_operation_in_short_period:
    'Une opération similaire a été effectuée récemment. Merci de patienter quelques minutes avant de réessayer.'
};

const operationNameDisplay = {
  wallet_funding: 'Recharge de wallet',
  send_money_option1: 'Envoi d’argent',
  send_money_option2: 'Envoi d’argent',
  ask_money: 'Demande d’argent',
  buy_airtime: 'Achat de crédit',
  buy_databundle: "Achat d'internet",
  buy_carrierbundle: 'Achat de forfait',
  buy_giftcard: 'Achat de carte cadeau',
  buy_bank_card: 'Achat de carte bancaire',
  buy_physical_bank_card: 'Achat de carte bancaire',
  fund_physical_bank_card: 'Recharge de carte bancaire',
  pay_fundraise: 'Paiement pour cagnotte',
  unknown: 'Inconnu'
};

export const serializer = new TransactionSerializer();
